import { UserGroupCategory, UserGroupKey } from "client/jspPlatformExperiment";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import jspPlatformApi from "client/portals";
import {
  isApiError,
  isErrorResponseBodyType,
  toJspApiError,
} from "models/utils/apiUtils";
import dayjs, { Dayjs } from "dayjs";

export const SITE_COLORS = [
  "rgb(120, 203, 255)",
  "rgb(246, 97, 97)",
  "rgb(147, 196, 125)",
  "rgb(255, 207, 132)",
  "rgb(180, 150, 220)",
  "rgb(255, 154, 162)",
  "rgb(160, 160, 160)",
  "rgb(100, 70, 50)",
];

interface UserGroupOptionsState {
  data: Record<number, UserGroupKey>;
  updatedAt: Dayjs | null | undefined;
  isLoading: boolean;
  error: string | null | undefined;
}

const initialState: UserGroupOptionsState = {
  data: {
    101: {
      id: 101,
      group_category: UserGroupCategory.SITE,
      group_name: "Excelsoins Bellagio",
    },
    102: {
      id: 102,
      group_category: UserGroupCategory.SITE,
      group_name: "Excelsoins Écluses",
    },
    103: {
      id: 103,
      group_category: UserGroupCategory.SITE,
      group_name: "Valleyfield",
    },
  },
  updatedAt: null,
  isLoading: false,
  error: null,
};

/**
 * Merge a batch of user group keys into the options map, keyed by id.
 */
const mergeUserGroupOptions = (
  state: UserGroupOptionsState,
  payload: UserGroupKey[] | null | undefined,
): UserGroupOptionsState => ({
  ...state,
  updatedAt: dayjs().subtract(dayjs.duration({ minutes: 10 })),
  data: {
    ...state.data,
    ...payload?.reduce<Record<number, UserGroupKey>>((acc, newUserGroup) => {
      acc[newUserGroup.id] = newUserGroup;
      return acc;
    }, {}),
  },
});

/**
 * Fetch every SITE-category user group and merge it into the options map.
 *
 * Used by the navbar site selector for the admin fallback: when an admin has no
 * assigned SITE groups, they pick from all sites returned here.
 */
const fetchSiteUserGroupOptions = createAsyncThunk(
  "userGroupOptions/fetchSiteUserGroupOptions",
  async (_: void, { rejectWithValue }) =>
    jspPlatformApi()
      .user.getUserGroupSearch({ query: { return_keys_only: true } })
      .then((res) =>
        (res.data ?? []).filter(
          (group) => group.group_category === UserGroupCategory.SITE,
        ),
      )
      .catch((error) => {
        let errMsg: string;
        const err = isApiError(error) ? toJspApiError(error) : null;
        if (err && isErrorResponseBodyType(err.body)) {
          errMsg = err.body.detail;
        } else {
          errMsg = "Unknown error!";
        }
        return rejectWithValue(errMsg);
      }),
);

const setUserGroupOptions = (
  state: UserGroupOptionsState,
  action: PayloadAction<UserGroupKey[] | null>,
): UserGroupOptionsState => mergeUserGroupOptions(state, action.payload);

export const userGroupOptionsSlice = createSlice({
  name: "userGroupOptionsSlice",
  initialState,
  reducers: {
    setUserGroupOptions,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSiteUserGroupOptions.pending, (state) => ({
      ...state,
      isLoading: true,
      error: null,
    }));
    builder.addCase(fetchSiteUserGroupOptions.fulfilled, (state, action) => ({
      ...mergeUserGroupOptions(state, action.payload),
      isLoading: false,
    }));
    builder.addCase(fetchSiteUserGroupOptions.rejected, (state, action) => ({
      ...state,
      isLoading: false,
      error: action.payload as string,
    }));
  },
});

export const userGroupActions = {
  ...userGroupOptionsSlice.actions,
  fetchSiteUserGroupOptions,
};

export const userGroupOptionsReducer = userGroupOptionsSlice.reducer;

export default userGroupOptionsReducer;
